/**
 * Fila do dia (Fase 4): SKUs fora do ritmo e SKUs com alavancas sem custo de margem; para cada anúncio ativo,
 * o passo de preço que o ritmo pede, com a MC antes de ads e as travas (docs/01 seção 5). Aprovar NÃO muda
 * preço no marketplace — registra a decisão no log do painel para a equipe aplicar e o painel medir o efeito.
 */
import { useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { Mc, Produto, Seta, StatusChip, TabelaOrdenavel } from './componentes'
import { registrarDecisao } from './fonte'
import { anuncioPrincipal, brl, brl2, dataBr, faixaJulgamento, filaFiltrada, filaHibrida, nomeLoja, num, pct } from './logic'
import type { ItemFila, NatalData, SkuNatal, SugestaoPreco } from './types'

type Pedido = { item: ItemFila; s: SugestaoPreco; acao: 'aprovar' | 'rejeitar' }

const ACAO: Record<SugestaoPreco['acao'], string> = { descer: '↓ descer um passo', subir: '↑ subir 5%', manter: 'manter', no_piso: 'já no piso' }

export function AbaFila({ d, ks, onAbrirSku, onMsg }: { d: NatalData; ks: SkuNatal[]; onAbrirSku: (sku: string) => void; onMsg: (m: string) => void }) {
  const fila = filaFiltrada(d, ks)
  const [pedido, setPedido] = useState<Pedido | null>(null)
  const [motivo, setMotivo] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [feitas, setFeitas] = useState<Record<string, string>>({})
  const comPreco = fila.filter((x) => x.sugestoes.some((s) => s.acao === 'descer' || s.acao === 'subir'))
  const { comEstoque, aguardando } = filaHibrida(fila)
  const faixa = faixaJulgamento(comEstoque)
  const [abertos, setAbertos] = useState<Set<string>>(new Set())
  const alternar = (k: string) => setAbertos((a) => { const n = new Set(a); if (n.has(k)) n.delete(k); else n.add(k); return n })

  const enviar = async () => {
    if (!pedido) return
    setEnviando(true)
    try {
      const { item, s, acao } = pedido
      await registrarDecisao({ sku: item.sku, anuncio: s.anuncio, loja: s.loja, acao, preco_de: s.preco, preco_para: s.novo_preco ?? s.preco,
        mc_de: s.mc, mc_para: s.mc_novo, motivo })
      setFeitas({ ...feitas, [`${item.sku}|${s.anuncio}`]: acao === 'aprovar' ? 'aprovada' : 'rejeitada' })
      onMsg(acao === 'aprovar'
        ? `Decisão registrada: ${s.anuncio} de ${brl2(s.preco)} para ${brl2(s.novo_preco)}. Aplique no marketplace; o efeito no ritmo é medido em 3–5 dias.`
        : `Sugestão rejeitada para ${s.anuncio}.`)
      setPedido(null); setMotivo('')
    } catch (e) { onMsg(`Não foi possível registrar: ${e instanceof Error ? e.message : String(e)}`) } finally { setEnviando(false) }
  }

  return (
    <Stack spacing={2.5}>
      {!d.premissas.mc_validada && (
        <Alert severity="warning">MC ainda não validada: os passos de preço aparecem com a margem calculada, mas nenhum pode ser aprovado até a premissa
          <b> mc_validada</b> virar verdadeira (CMV do lote novo no <code>cmvs</code> + conferência da contabilidade).</Alert>
      )}
      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.5} alignItems={{ xs: 'stretch', xl: 'flex-start' }}>
        <Card variant="outlined" sx={{ flex: 2.4, minWidth: 0, width: '100%' }}><CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
            <Box><Typography variant="h6">Fila do dia</Typography>
              <Typography variant="caption" color="text.secondary">Atrás primeiro, depois estoque parado a custo. Clique numa linha para ver os anúncios e aprovar. Você decide.</Typography></Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${comEstoque.length} com estoque`} />
              {aguardando.length > 0 && <Chip size="small" variant="outlined" label={`${aguardando.length} aguardando chegada`} />}
              <Chip size="small" color="primary" variant="outlined" label={`${comPreco.length} com passo de preço`} />
              <Chip size="small" variant="outlined" label={`${d.rodadas_semana} de ${d.premissas.max_rodadas_preco_semana} rodadas na semana`} /></Stack>
          </Stack>
          {faixa && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              <b>Começo de temporada.</b> O ritmo só é julgado depois que a meta acumulada do SKU passa de {pct(d.premissas.alvo_minimo_desvio)} da meta
              total. Para os SKUs com estoque isso acontece entre <b>{dataBr(faixa[0])}</b> e <b>{dataBr(faixa[1])}</b>. Até lá a fila mostra só
              alavancas sem custo de margem, sem passo de preço.
            </Alert>
          )}
          {!fila.length
            ? <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Nenhum SKU fora do ritmo nem alavanca pendente no filtro.</Typography>
            : (
              <TableContainer sx={{ mt: 1.5, maxHeight: '70vh', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    {['SKU', 'Status', 'Ritmo 7d', 'Estoque a custo', 'Ação sugerida', 'Preço → passo', 'MC → passo', 'Anúncios'].map((h, i) => (
                      <TableCell key={h} align={i >= 3 && i !== 4 ? 'right' : 'left'} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{h}</TableCell>
                    ))}
                  </TableRow></TableHead>
                  <TableBody>
                    {comEstoque.map((x) => <LinhaSku key={x.sku} x={x} d={d} aberto={abertos.has(x.sku)} onAlternar={() => alternar(x.sku)}
                      onAbrirSku={onAbrirSku} feitas={feitas} onPedir={(s, acao) => setPedido({ item: x, s, acao })} />)}
                    {aguardando.length > 0 && (
                      <>
                        <TableRow hover onClick={() => alternar(AGUARDANDO)} sx={{ cursor: 'pointer', bgcolor: 'action.hover' }}>
                          <TableCell colSpan={8}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Seta aberta={abertos.has(AGUARDANDO)} />
                              <Typography variant="body2"><b>Aguardando chegada</b> · {aguardando.length} SKUs sem estoque no galpão nem no Full · ritmo e preço só depois que o lote chegar</Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        <TableRow><TableCell colSpan={8} sx={{ py: 0, borderBottom: abertos.has(AGUARDANDO) ? undefined : 0 }}>
                          <Collapse in={abertos.has(AGUARDANDO)} unmountOnExit>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 1.5 }}>
                              {aguardando.map((x) => <Chip key={x.sku} size="small" variant="outlined" title={x.descricao} onClick={() => onAbrirSku(x.sku)}
                                label={`${x.sku}${x.proxima_chegada ? ` · chega ${dataBr(x.proxima_chegada)}` : ''}`} />)}
                            </Box>
                          </Collapse>
                        </TableCell></TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            Preço e MC da linha = anúncio principal do SKU (o que mais faturou em 7 dias). Passo ↓ = {pct(d.premissas.passo_baixa_pct_espaco)} do espaço até o piso (MC {pct(d.premissas.mc_piso_pct)} até {dataBr(d.premissas.data_liquidacao)}, depois {pct(d.premissas.mc_pos_liquidacao)}; perenes ficam no piso normal).
            Passo ↑ = +{pct(d.premissas.passo_alta_pct)}. Uma mudança por anúncio a cada {d.premissas.intervalo_min_mudanca_dias} dias; no máximo {d.premissas.max_rodadas_preco_semana} rodadas por semana.
            MC antes de ads (ads entra no DRE abaixo da MC): ML com comissão e frete cotados ao vivo para cada preço; Shopee pelo histórico de 90 dias. Aprovar só registra a decisão: o preço é aplicado por você no marketplace.
          </Typography>
        </CardContent></Card>
      </Stack>
      <Typography variant="caption" color="text.secondary">Os alertas do dia estão na aba Briefing.</Typography>

      <Card variant="outlined"><CardContent>
        <Typography variant="h6">Log de decisões</Typography>
        <Typography variant="caption" color="text.secondary">Efeito = ritmo de hoje − ritmo no dia da decisão, medido a partir de 3 dias depois (docs/01 seção 7).</Typography>
        <TabelaOrdenavel vazio="Nenhuma decisão registrada ainda." linhas={d.decisoes} colunas={[
          { label: 'Dia', valor: (r) => r.dia, render: (r) => dataBr(r.dia) },
          { label: 'SKU', valor: (r) => r.sku, render: (r) => <b>{r.sku || (r.tipo === 'container' ? 'Container' : r.tipo === 'meta_loja' ? 'Meta das lojas' : '—')}</b> },
          { label: 'Anúncio', valor: (r) => r.anuncio, render: (r) => (r.loja ? `${r.anuncio} · ${nomeLoja(d, r.loja)}` : r.anuncio) },
          { label: 'Decisão', valor: (r) => r.acao, render: (r) => (r.de_txt != null ? <Chip size="small" color="info" label="ajuste" /> : <Chip size="small" color={r.acao === 'aprovar' ? 'success' : 'default'} label={r.acao === 'aprovar' ? 'aprovada' : 'rejeitada'} />) },
          { label: 'De → para', valor: (r) => r.preco_para - r.preco_de, render: (r) => (r.de_txt != null ? <Box sx={{ maxWidth: 320, whiteSpace: 'normal', textAlign: 'right' }}>{r.de_txt} → {r.para_txt}</Box> : `${brl2(r.preco_de)} → ${brl2(r.preco_para)}`), alinhar: 'right' },
          { label: 'Ritmo no dia', valor: (r) => r.ritmo_no_dia, render: (r) => (r.ritmo_no_dia == null ? '—' : r.ritmo_no_dia.toFixed(2).replace('.', ',')), alinhar: 'right' },
          { label: 'Efeito', valor: (r) => r.efeito ?? null, render: (r) => (r.efeito == null ? <Typography variant="caption" color="text.secondary">medindo</Typography> : `${r.efeito > 0 ? '+' : ''}${r.efeito.toFixed(2).replace('.', ',')}`), alinhar: 'right' },
          { label: 'Quem', valor: (r) => r.quem, render: (r) => r.quem },
          { label: 'Motivo', render: (r) => <Box sx={{ maxWidth: 260, whiteSpace: 'normal' }}>{r.motivo || '—'}</Box> },
        ]} />
      </CardContent></Card>

      <Dialog open={!!pedido} onClose={() => !enviando && setPedido(null)} maxWidth="sm" fullWidth sx={{ zIndex: (t) => t.zIndex.modal + 4 }}>
        <DialogTitle>{pedido?.acao === 'aprovar' ? 'Aprovar mudança de preço' : 'Rejeitar sugestão'}</DialogTitle>
        <DialogContent>
          {pedido && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2"><b>{pedido.item.sku}</b> · {pedido.s.anuncio} ({nomeLoja(d, pedido.s.loja)})</Typography>
              <Typography variant="body2">{brl2(pedido.s.preco)} (MC {pct(pedido.s.mc, 1)}) → <b>{brl2(pedido.s.novo_preco)}</b> (MC {pct(pedido.s.mc_novo, 1)})</Typography>
              {pedido.acao === 'aprovar' && <Alert severity="info">O painel não altera o anúncio. A decisão fica no log; aplique o preço no marketplace.</Alert>}
              <TextField label="Motivo (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} multiline minRows={2} inputProps={{ maxLength: 300 }} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPedido(null)} disabled={enviando}>Cancelar</Button>
          <Button variant="contained" onClick={() => void enviar()} disabled={enviando}>{pedido?.acao === 'aprovar' ? 'Registrar aprovação' : 'Registrar rejeição'}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

const AGUARDANDO = '__aguardando'


/** Linha do SKU (formato do mockup aprovado em 06/10/2026) e, aberta, os anúncios com Aprovar/Rejeitar. */
function LinhaSku({ x, d, aberto, onAlternar, onAbrirSku, feitas, onPedir }: {
  x: ItemFila; d: NatalData; aberto: boolean; onAlternar: () => void; onAbrirSku: (sku: string) => void
  feitas: Record<string, string>; onPedir: (s: SugestaoPreco, acao: 'aprovar' | 'rejeitar') => void
}) {
  const p = anuncioPrincipal(x)
  const mexe = p != null && (p.acao === 'descer' || p.acao === 'subir')
  const r = x.meta7 ? Math.min(1, x.vend7 / x.meta7) : 0
  const cor = x.status === 'ATRAS' ? 'error' : x.status === 'A_FRENTE' ? 'warning' : x.status === 'NO_RITMO' ? 'success' : 'inherit'
  return (
    <>
      <TableRow hover onClick={onAlternar} sx={{ cursor: 'pointer', '& > td': { borderBottom: aberto ? 0 : undefined } }}>
        <TableCell>
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            <Seta aberta={aberto} />
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button size="small" onClick={(e) => { e.stopPropagation(); onAbrirSku(x.sku) }} sx={{ fontWeight: 700, p: 0, minWidth: 0 }}>{x.sku}</Button>
                {x.custo_fonte !== 'cmvs_lote_novo' && <Chip size="small" color="warning" variant="outlined" label="custo estimado" title={x.custo_fonte} />}
                {x.ultima_decisao && <Chip size="small" variant="outlined" label={`última decisão ${dataBr(x.ultima_decisao.dia)}`} />}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 220, whiteSpace: 'normal' }}>{x.descricao}</Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <StatusChip s={x.status} parado={d.skus.find((k) => k.sku === x.sku)?.calc.parado} />
          {x.status === 'SEM_LEITURA' && x.julga_em && <Typography variant="caption" color="text.secondary" display="block">julga a partir de {dataBr(x.julga_em)}</Typography>}
        </TableCell>
        <TableCell sx={{ minWidth: 150 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <LinearProgress variant="determinate" value={r * 100} color={cor} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{num(x.vend7)} / {num(x.meta7)} pç</Typography>
          </Stack>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {brl(x.estoque_custo)}
          {x.estoque_pecas != null && <Typography variant="caption" color="text.secondary" display="block">{num(x.estoque_pecas)} pç</Typography>}
        </TableCell>
        <TableCell>
          {mexe
            ? <Chip size="small" color="warning" label={ACAO[p.acao]} />
            : x.alavancas.length
              ? <Stack direction="row" spacing={0.5} alignItems="center"><Chip size="small" color="primary" variant="outlined" label={x.alavancas[0]} />
                  {x.alavancas.length > 1 && <Typography variant="caption" color="text.secondary">+{x.alavancas.length - 1}</Typography>}</Stack>
              : '—'}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {p ? <>{brl2(p.preco)} → <b>{mexe ? brl2(p.novo_preco) : '—'}</b></> : <Typography variant="caption" color="text.secondary">sem anúncio ativo</Typography>}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {p ? (p.mc == null ? <Typography variant="caption" color="text.secondary">{p.erro}</Typography> : <><Mc v={p.mc} /> → {mexe ? <Mc v={p.mc_novo} /> : '—'}</>) : '—'}
        </TableCell>
        <TableCell align="right">{num(x.sugestoes.length)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, bgcolor: aberto ? 'action.hover' : undefined, borderBottom: aberto ? undefined : 0 }}>
          <Collapse in={aberto} unmountOnExit>
            <Stack spacing={1} sx={{ py: 1.5, pl: 3 }}>
              {x.alavancas.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ mr: 0.5 }}><b>Sem custo de margem:</b></Typography>
                  {x.alavancas.map((a) => <Chip key={a} size="small" color="primary" variant="outlined" label={a} />)}
                </Box>
              )}
              <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <TabelaOrdenavel alturaMax={null} vazio="Nenhum anúncio ativo deste SKU em nenhuma loja." linhas={x.sugestoes} colunas={[
                  { label: 'Anúncio', valor: (s) => s.titulo, render: (s) => <Produto a={{ titulo: s.titulo, link: s.link, imagem: s.imagem ?? null, id: s.anuncio }} largura={300} /> },
                  { label: 'Loja', valor: (s) => s.loja, render: (s) => <>{nomeLoja(d, s.loja)}{s.full && <Typography variant="caption" color="text.secondary" display="block">Full</Typography>}</> },
                  { label: 'Vendas 7d', valor: (s) => s.pecas7 ?? 0, alinhar: 'right', render: (s) => <>{num(s.pecas7 ?? 0)} pç<Typography variant="caption" color="text.secondary" display="block">{brl(s.fat7 ?? 0)}</Typography></> },
                  { label: 'Preço', valor: (s) => s.preco, render: (s) => brl2(s.preco), alinhar: 'right' },
                  { label: 'MC atual', valor: (s) => s.mc, alinhar: 'right', render: (s) => (s.mc == null ? <Typography variant="caption" color="text.secondary">{s.erro}</Typography> : <span title={s.fonte_taxas}><Mc v={s.mc} /></span>) },
                  { label: 'Ritmo pede', valor: (s) => s.acao, render: (s) => (x.status === 'SEM_LEITURA' ? <Typography variant="caption" color="text.secondary">aguardando leitura do ritmo</Typography> : ACAO[s.acao]) },
                  { label: 'Preço → novo', valor: (s) => s.novo_preco ?? s.preco, render: (s) => (s.novo_preco ? <>{brl2(s.preco)} → <b>{brl2(s.novo_preco)}</b></> : '—'), alinhar: 'right' },
                  { label: 'MC → nova', valor: (s) => s.mc_novo ?? s.mc, render: (s) => (s.mc_novo == null ? '—' : <><Mc v={s.mc} /> → <Mc v={s.mc_novo} /></>), alinhar: 'right' },
                  { label: 'Piso', valor: (s) => s.piso, render: (s) => brl2(s.piso), alinhar: 'right' },
                  { label: 'Decisão', render: (s) => {
                    const feita = feitas[`${x.sku}|${s.anuncio}`]
                    if (feita) return <Chip size="small" color="success" label={feita} />
                    if (s.acao !== 'descer' && s.acao !== 'subir') return '—'
                    return (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Button size="small" variant="contained" disabled={!s.aprovavel} onClick={() => onPedir(s, 'aprovar')}>Aprovar</Button>
                        <Button size="small" onClick={() => onPedir(s, 'rejeitar')}>Rejeitar</Button>
                        {s.bloqueios.length > 0 && <Typography variant="caption" color="warning.main" sx={{ whiteSpace: 'normal', maxWidth: 220 }}>{s.bloqueios.join(' · ')}</Typography>}
                      </Stack>
                    )
                  } },
                ]} />
              </Box>
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}
